import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "./src/app/AppShell.jsx";
import { canAccess, defaultScreenFor, isPickerJob, isResponderJob } from "./src/app/access";
import { RequireAccess } from "./src/app/RequireAccess.jsx";
import { SessionGate } from "./src/app/SessionGate.jsx";
import { UserAdmin, ZephyrStaffAdmin } from "./src/components/admin/AdminPages.jsx";
import { AdminPermissionsMatrix } from "./src/components/admin/AdminPermissionsMatrix.jsx";
import { AuditLogPage } from "./src/components/admin/AuditLogPage.jsx";
import { ImportPage } from "./src/components/admin/ImportPage.jsx";
import { InvitesAdmin } from "./src/components/admin/InvitesAdmin.jsx";
import { LookupListsAdmin } from "./src/components/admin/LookupListsAdmin.jsx";
import { ProductMappingAdmin } from "./src/components/admin/ProductMappingAdmin.jsx";
import { ResourcePagesAdmin } from "./src/components/admin/ResourcePagesAdmin.jsx";
import { SalesOrdersAdmin } from "./src/components/admin/SalesOrdersAdmin.jsx";
import { SchoolsAdmin } from "./src/components/admin/SchoolsAdmin.jsx";
import { SORulesAdmin } from "./src/components/admin/SORulesAdmin.jsx";
import { SupplierAdmin } from "./src/components/admin/SupplierAdmin.jsx";
import { SystemSetupAdmin } from "./src/components/admin/SystemSetupAdmin.jsx";
import { TemplatesAdmin } from "./src/components/admin/TemplatesAdmin.jsx";
import { TopUpProductMappingAdmin } from "./src/components/admin/TopUpProductMappingAdmin.jsx";
import { TopUpSORulesAdmin } from "./src/components/admin/TopUpSORulesAdmin.jsx";
import { UnleashedProductsAdmin } from "./src/components/admin/UnleashedProductsAdmin.jsx";
import { UnleashedPushListAdmin } from "./src/components/admin/UnleashedPushListAdmin.jsx";
import {
  AdminDash,
  PickersPage,
  ShelterDash,
  ToDoPage,
} from "./src/components/dashboard/DashboardPages.jsx";
import { ReportsPage } from "./src/components/dashboard/ReportsPage.jsx";
import { StatisticsPage } from "./src/components/dashboard/StatisticsPage.jsx";
import TopUpTaskListPage from "./src/components/dashboard/TopUpTaskListPage.jsx";
import { RequestWizard } from "./src/components/form/RequestWizard.jsx";
import { TopUpPrefillWizard } from "./src/components/form/TopUpPrefillWizard.jsx";
import { ReqDetail } from "./src/components/request/RequestDetail.jsx";
import { CalendarPage } from "./src/components/resources/CalendarPage.jsx";
import { GeneralPage } from "./src/components/resources/GeneralPage.jsx";
import { InvoicingPage } from "./src/components/resources/InvoicingPage.jsx";
import { ResourceDocRedirect } from "./src/components/resources/ResourceDocRedirect.jsx";
import { RespondersPage } from "./src/components/resources/RespondersPage.jsx";
import { StaffDirectory } from "./src/components/resources/StaffDirectory.jsx";
import { getConfigValue } from "./src/data/portalConfig";
import { getRequestViewRowById } from "./src/data/requests";
import { useAuthSession } from "./src/hooks/useAuthSession";
import { useInactivityLogout } from "./src/hooks/useInactivityLogout";
import { detectRecoveryFromUrl, usePasswordRecovery } from "./src/hooks/usePasswordRecovery";
import { usePortalData } from "./src/hooks/usePortalData";
import { useRequestPersistence } from "./src/hooks/useRequestPersistence";
import {
  ACCESS_LOCKOUT_KEY,
  isRoleLockedOut,
  normalizeAccessLockout,
} from "./src/services/accessLockout";
import { alertDialog } from "./src/services/dialogService";
import { NEW_REQUESTS_KEY, normalizeNewRequestsEnabled } from "./src/services/newRequestSwitch";
import {
  SESSION_TIMEOUT_KEY,
  timersForUser,
  warnLeadMinutesForUser,
} from "./src/services/sessionTimeout";
import { logAudit } from "./src/utils/auditLog";
import { ROLES } from "./src/utils/enums";
import { findRequestInList } from "./src/utils/findRequestInList";
import { dbToReq } from "./src/utils/helpers";
import { logger } from "./src/utils/logger";
import { isStaffEmailDomain, providerFromAccessToken } from "./src/utils/loginMethod";
import { isMfaGatedRole, needsMfaEnrollment } from "./src/utils/mfa";
import { installNumericInputGuard } from "./src/utils/numericInput";
import { logPageLoad } from "./src/utils/perf";
import { defaultPageForUser, loadSessionUser } from "./src/utils/sessionUser";
import { getSupabaseClient } from "./src/utils/supabase";

// ============================================================
// ZEPHYR EDUCATION PORTAL - Stage 3 (Supabase Integration)
// v17 child-centric \u2014 feesOpen fix, one-row identity fields
// ============================================================
// Phase 4: Performance monitoring (logs to browser console)
logPageLoad();

// Supabase project: vjodadskpntevyhltceg (Sydney, ap-southeast-2)
// Auth: Supabase Auth (email/password)
// DB: requests table with RLS
// Storage: zephyr-files bucket
// ============================================================

// formatRef, formatRefForUnleashed now imported from src/utils/helpers.js

// Supabase env vars + client creation now live in src/utils/supabase.ts (the
// single source of truth) — see docs/supabase-client-singleton-plan.md.

// ── GOOGLE PLACES AUTOCOMPLETE ──────────────────────────────
const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;
if (!GOOGLE_MAPS_KEY) {
  logger.warn("VITE_GOOGLE_MAPS_KEY is not set -- address autocomplete will be disabled.");
}

// Load Google Maps using recommended dynamic import method
(function () {
  if (window.google?.maps) return;
  if (document.querySelector("script[data-gmaps]")) return;
  var script = document.createElement("script");
  script.setAttribute("data-gmaps", "1");
  script.src =
    "https://maps.googleapis.com/maps/api/js?key=" +
    GOOGLE_MAPS_KEY +
    "&loading=async&libraries=places&callback=__gmapsReady";
  script.async = true;
  script.onerror = function () {
    logger.warn("Google Maps failed to load -- address autocomplete disabled");
  };
  window.__gmapsReady = function () {
    logger.debug("Google Maps Places API ready");
  };
  document.head.appendChild(script);
})();

// Load jsPDF for authority doc PDF generation
(function () {
  if (window.jspdf) return;
  if (document.querySelector("script[data-jspdf]")) return;
  var s = document.createElement("script");
  s.setAttribute("data-jspdf", "1");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
  s.async = true;
  document.head.appendChild(s);
})();

// Supabase client is the single source of truth in src/utils/supabase.ts:
// created + memoized there on the first getSupabaseClient() call. Portal just
// reads it via getSupabaseClient() — it neither creates nor stores the client.

// Parse YYYY-MM-DD as local date \u2014 avoids UTC timezone shift
// fmtDateLocal, MONTHS now imported from src/utils/helpers.js

// --- EMAIL NOTIFICATIONS ----------------------------------------
// Calls a Supabase Edge Function named "send-email".
// Deploy that function to enable real sending (see handover doc).
// Send email via SMTP2GO edge function (for shelter notifications only)
// sendEmail, createTask, createPickerTask now imported from src/utils/actions.js

// Authority doc functions + buildRequestSummary now imported from src/utils/authorityDoc.js and src/utils/helpers.js

// Colour palette: the shared `C` object lives in src/utils/ui.jsx; portal.jsx no
// longer keeps its own duplicate. The one shell background below uses the literal.

// ── STANDARD COSTS ─────────────────────────────────────────────────────────
// Loaded from portal_config table (key = "standard_costs").
// Falls back to these defaults if not yet configured.

// --- SHARED UI COMPONENTS -------------------------------------

// --- FORM TABS ------------------------------------------------

// --- DASHBOARD ------------------------------------------------

// ── IMPORT PAGE ─────────────────────────────────────────────────────────────

// ─── TO DO LIST (Responders only) ──────────────────────────────────────────────────

// --- REQUEST DETAIL -------------------------------------------

// --- REQUEST DETAIL -------------------------------------------

// --- APP ROOT -------------------------------------------------

// Shown inside the app shell while a deep-linked /requests/:id is being fetched
// (the request isn't in the loaded list yet).
function RequestLoading() {
  return (
    <div style={{ padding: 40, textAlign: "center", color: "#607D79", fontSize: 15 }}>
      Loading request…
    </div>
  );
}

export default function App() {
  // Remove spinner arrows from all number inputs globally
  React.useEffect(function () {
    var s = document.createElement("style");
    s.textContent =
      "input[type=number]::-webkit-outer-spin-button,input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}";
    document.head.appendChild(s);
    return function () {
      if (s.parentNode) s.parentNode.removeChild(s);
    };
  }, []);
  // Prevent browser from opening dropped files (global safety net)
  React.useEffect(function () {
    function preventDrop(e) {
      e.preventDefault();
    }
    window.addEventListener("dragover", preventDrop);
    window.addEventListener("drop", preventDrop);
    return function () {
      window.removeEventListener("dragover", preventDrop);
      window.removeEventListener("drop", preventDrop);
    };
  }, []);
  // Block "e"/"E"/"+"/"-" in number inputs so price/cost fields only take digits
  // (see src/utils/numericInput.ts). One global guard covers every number field.
  React.useEffect(function () {
    return installNumericInputGuard();
  }, []);
  const [user, setUser] = useState(null);
  // The address bar IS the source of truth for the active top-level screen:
  // <Routes> renders it directly (Phase 4). The request sub-views (new / detail /
  // edit) are still state-driven early-returns mirrored to /requests/... by the
  // URL<->state sync below — real URLs, Back/Forward, deep links and shareable
  // /requests/:id links.
  const navigate = useNavigate();
  const location = useLocation();
  // Top-level navigation is now the URL itself (Phase 4 slice 2a-ii): <Routes>
  // renders the active screen from the address bar, so there is no `page` state.
  // navTo(screen) is the single "go to a top-level screen" helper; it's handed to
  // useAuthSession as `setPage` (the hook is unchanged — it just navigates now).
  function navTo(screen) {
    navigate(`/${screen}`);
  }
  // Remembers the last top-level URL so closing a request sub-view returns to the
  // screen it was opened from (replaces the old `page`'s return role). Updated by
  // the effect below whenever the address bar is on a top-level (non-/requests) path.
  var lastTopLevelPathRef = React.useRef(
    window.location.pathname.startsWith("/requests") ? null : window.location.pathname
  );
  useEffect(
    function () {
      if (!location.pathname.startsWith("/requests")) {
        lastTopLevelPathRef.current = location.pathname;
      }
    },
    [location.pathname]
  );
  // A /requests/... URL we landed on while signed OUT. We hold the address bar
  // there through the login flow (instead of hopping to the default screen), then
  // resolve straight to the request via the URL→state sync. Captured on mount;
  // cleared once the user is signed in + ready. Declared up here so the login
  // landing (setPage handed to useAuthSession) can defer to it.
  const pendingDeepLink = React.useRef(
    window.location.pathname.startsWith("/requests/") ? window.location.pathname : null
  );
  const [mfaRequired, setMfaRequired] = useState(false); // staff must enroll MFA
  const [mfaChallenge, setMfaChallenge] = useState(false); // MFA code entry during login
  const [showSecurity, setShowSecurity] = useState(false); // security panel
  const [unactionedTaskCount, setUnactionedTaskCount] = useState(0);
  const [pickerTaskCount, setPickerTaskCount] = useState(0);
  const [topUpTaskCount, setTopUpTaskCount] = useState(0);
  // todoReturnPage state is retired (sub-view return uses lastTopLevelPathRef); the
  // setter is kept only because useAuthSession still calls it. Value unread.
  const [, setTodoReturnPage] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  // Admin-configurable idle-logout periods (Manage → System Setup → Config),
  // separate for DFV Services vs Zephyr staff. Raw portal_config value; resolved
  // to warn/logout ms for the current user below. null until loaded → the hook's
  // built-in 25/30-min defaults apply.
  const [sessionTimeoutCfg, setSessionTimeoutCfg] = useState(null);
  // Starts true so intake is never briefly blocked while the config loads.
  const [newRequestsEnabled, setNewRequestsEnabled] = useState(true);
  const saveDraftRef = React.useRef(null);
  var pendingDraftIdRef = React.useRef(null); // tracks the draft row created during new request wizard
  var pendingDraftRefNoRef = React.useRef(null); // tracks the ref_no of the in-progress draft (must not be sent as null on subsequent PATCHes)

  // ── Request sub-views are derived from the URL (Phase 4 2b) ──────────────────
  // No selected/newReq/editingDraft state: the address bar is the single source.
  // A deep-linked request not yet in the list is fetched into `requests` on demand
  // (below) — so it's the SAME object the persistence handlers update; a separate
  // cache would lose in-place edits (the responder price-edit bug).
  // Match by string id: the URL param is a string ("294") but request ids are
  // numbers (294), so findRequestInList's strict === would miss them.
  function resolveReq(id) {
    if (!id) return null;
    return (
      requests.find(function (r) {
        return String(r.id) === String(id);
      }) || null
    );
  }
  var subViewPath = location.pathname;
  var newReq = subViewPath === "/requests/new";
  var editMatch = subViewPath.match(/^\/requests\/([^/]+)\/edit$/);
  var editingDraft = editMatch ? resolveReq(editMatch[1]) : null;
  var detailMatch = !editMatch && subViewPath.match(/^\/requests\/([^/]+)$/);
  var selected = detailMatch ? resolveReq(detailMatch[1]) : null;
  function returnTarget() {
    return lastTopLevelPathRef.current || `/${defaultScreenFor(user)}`;
  }
  // Setter SHIMS — the hooks (useAuthSession / useRequestPersistence) still call
  // setSel/setNewReq/setEditingDraft to transition the view; these NAVIGATE instead
  // of setting state. CRITICAL: skip the navigate when already on the target URL —
  // otherwise the setSel(savedReq) that fires on every in-memory edit re-navigates
  // to the current URL each keystroke, stealing focus / resetting the field.
  function setSel(req) {
    if (req) {
      var t = `/requests/${req.id}`;
      if (location.pathname !== t) navigate(t);
    } else if (!pendingDeepLink.current && detailMatch) navigate(returnTarget());
  }
  function setNewReq(v) {
    if (v) {
      if (location.pathname !== "/requests/new") navigate("/requests/new");
    } else if (!pendingDeepLink.current && newReq) navigate(returnTarget());
  }
  function setEditingDraft(d) {
    if (d) {
      var t = `/requests/${d.id}/edit`;
      if (location.pathname !== t) navigate(t);
    } else if (!pendingDeepLink.current && editMatch) navigate(returnTarget());
  }
  // db-209 (deleted-draft resubmit, 21 Oct 2026): a fresh visit to the new-request
  // wizard must never inherit a draft id from an earlier visit — one left via a
  // header tab or browser Back (neither clears the ref) and possibly deleted since.
  // A stale id made the submit UPDATE that old row instead of inserting a new one.
  useEffect(() => {
    if (newReq) {
      pendingDraftIdRef.current = null;
      pendingDraftRefNoRef.current = null;
    }
  }, [newReq]);

  // Request create / save / submit handlers live in a tested hook
  // (src/hooks/useRequestPersistence.ts). The App owns the state; the hook owns
  // the persistence logic. saveChildrenRPC stays internal to the hook.
  const { saveDraft, updateDraft, updateRequest } = useRequestPersistence({
    user,
    editingDraft,
    setRequests,
    setNewReq,
    setSel,
    setEditingDraft,
    pendingDraftIdRef,
    pendingDraftRefNoRef,
  });
  // List / count loaders extracted to a tested hook (src/hooks/usePortalData.ts).
  // They read the live client via getSupabaseClient(), so they work even when
  // called synchronously inside the session-restore effect (before re-render).
  const pd = usePortalData({
    setRequests,
    setUnactionedTaskCount,
    setPickerTaskCount,
    setTopUpTaskCount,
  });
  // Stable loader identities (A4, live-lists overhaul): usePortalData is a plain factory
  // that returns fresh closures every render. Wrap them so their identity is constant —
  // this lets the Session/Requests context values below memoize (an unrelated re-render
  // no longer rebuilds requestsValue and re-renders every consumer), and stops the
  // effects/hooks that receive these loaders from re-running each render. The latest
  // closures are held in a ref so a wrapper always calls the current implementation.
  const pdRef = React.useRef(pd);
  pdRef.current = pd;
  const loadRequests = React.useCallback(function (u) {
    return pdRef.current.loadRequests(u);
  }, []);
  const loadUnactionedTasks = React.useCallback(function () {
    return pdRef.current.loadUnactionedTasks();
  }, []);
  const loadPickerTasks = React.useCallback(function () {
    return pdRef.current.loadPickerTasks();
  }, []);
  const loadTopUpTasks = React.useCallback(function () {
    return pdRef.current.loadTopUpTasks();
  }, []);
  // "View Requests" from the Shelters & Users admin list navigates to /admin with
  // the shelter name in router state (replaces the old viewShelterRequests
  // CustomEvent bridge). AdminDash's defaultShelterFilter is fed from that state
  // below, and onFilterApplied clears it so it doesn't re-apply on refresh/back.

  React.useEffect(() => {
    const client = getSupabaseClient();
    // Detect password-recovery link click. Two paths:
    //  (a) URL hash contains type=recovery (legacy implicit flow)
    //  (b) URL search contains ?reset=1 (we set this on the redirectTo when
    //      calling resetPasswordForEmail -- reliable for PKCE flow and even
    //      when an existing session would otherwise swallow the auth event)
    try {
      var hash = window.location.hash || "";
      var search = window.location.search || "";
      // Classify the recovery URL (pure); perform the side effects here.
      var rec = detectRecoveryFromUrl(hash, search);
      if (rec.kind === "expired" || rec.kind === "error") {
        // No session to preserve on an error -- safe to clear the URL.
        // Use react-router navigate (replace) rather than a bare
        // history.replaceState so the router's own location stays in sync
        // (Phase 4 derives the rendered view from location.pathname).
        alertDialog({ message: rec.alert });
        navigate(window.location.pathname, { replace: true });
      } else if (rec.kind === "recovery") {
        // For a genuine recovery link, do NOT clear the hash -- the Supabase SDK
        // needs the access_token + type=recovery params to establish the session.
        setPasswordRecovery(true);
        if (rec.stripResetParam) {
          // Strip only our own ?reset=1 flag (not Supabase's params).
          // Navigate via react-router (replace) so its location stays in sync;
          // KEEP the hash -- the Supabase SDK needs the access_token +
          // type=recovery params in the hash to establish the recovery session.
          var newSearch = search.replace(/[?&]reset=1/, "").replace(/^&/, "?");
          if (newSearch === "?") newSearch = "";
          navigate(
            {
              pathname: window.location.pathname,
              search: newSearch,
              hash: window.location.hash,
            },
            { replace: true }
          );
        }
      }
    } catch (_e) {
      /* ignore */
    }
    client.auth.getSession().then(async function ({ data: { session } }) {
      if (session) {
        // Fetch + assemble the session user (profiles + user_shelters + shelters +
        // delivery_addresses). The fetch orchestration is unit-tested in
        // src/utils/sessionUser.ts; null means the profile couldn't be loaded.
        var u = await loadSessionUser(session.user);
        if (u) {
          // A deactivated profile must not establish a session, even if the Auth
          // ban never took (defense in depth — docs/account-shelter-lifecycle-plan.md
          // Phase 3). Sign out + tell them, mirroring the wrong-method rejection below.
          if (u.deactivated) {
            logAudit("session_refused_deactivated", {
              user_id: u.id,
              email: u.email,
              via: "restore",
            });
            await client.auth.signOut();
            setUser(null);
            setLoading(false);
            await alertDialog({
              title: "No longer active",
              message: "This user is no longer active.",
            });
            return;
          }
          // A shelter caseworker with no user_shelters membership has no shelter
          // access - refuse the session (docs/retire-profile-shelter-code-plan.md
          // Phase 2). Internal roles are never flagged.
          if (u.noShelterAccess) {
            logAudit("session_refused_no_shelter", {
              user_id: u.id,
              email: u.email,
              via: "restore",
            });
            await client.auth.signOut();
            setUser(null);
            setLoading(false);
            await alertDialog({
              title: "No DFV Service access",
              message:
                "This account isn't linked to a DFV Service. Please contact an administrator.",
            });
            return;
          }
          const provider = providerFromAccessToken(session.access_token);
          // Google SSO (docs/google-sso-plan.md §0): the login method is DERIVED from
          // the email DOMAIN, gated by the VITE_GOOGLE_SSO flag. When on, a Zephyr
          // Workspace email must arrive via Google, and any other email must arrive
          // via password — reject a mismatched session. OFF (default) → no rejection,
          // so today's password / email-link / refresh logins are unaffected.
          const ssoOn = import.meta.env.VITE_GOOGLE_SSO === "true";
          const emailIsStaff = isStaffEmailDomain(u.email);
          if (
            ssoOn &&
            ((emailIsStaff && provider !== "google") || (!emailIsStaff && provider === "google"))
          ) {
            logAudit("login_method_rejected", {
              user_id: u.id,
              email: u.email,
              method: emailIsStaff ? "google" : "password",
              via: provider,
            });
            await client.auth.signOut();
            setUser(null);
            setLoading(false);
            await alertDialog({
              title: "Wrong sign-in method",
              message: emailIsStaff
                ? "Zephyr staff sign in with Google Workspace."
                : "This account signs in with your email and password.",
            });
            return;
          }
          // Access lockout (Manage → System Setup → Access): a SuperUser can lock
          // whole role groups out. Enforce on session RESTORE too, so an
          // already-signed-in user of a locked group is signed out on their next
          // page load (John, 25 Jul 2026 — "login AND active sessions"). SuperUsers
          // are never locked out, so the switches can always be reversed.
          const lockout = normalizeAccessLockout(await getConfigValue(ACCESS_LOCKOUT_KEY));
          if (isRoleLockedOut(u.role, lockout)) {
            logAudit("access_lockout_blocked", {
              user_id: u.id,
              email: u.email,
              role: u.role || "",
              via: "restore",
            });
            await client.auth.signOut();
            setUser(null);
            setLoading(false);
            await alertDialog({ title: "Access unavailable", message: lockout.message });
            return;
          }
          // Enforce MFA enrolment on session RESTORE too, not just explicit login:
          // a staff/combined user can establish a session via an email/recovery link
          // (or a refresh) without going through login(), so without this they'd be
          // inside the app with no MFA. Mirror login()'s behaviour — set the user +
          // mfaRequired and skip the data load; it loads after enrolment, via
          // MfaSetupRequired -> PostLoginSelection -> onPostLoginComplete.
          // A Google session skips the portal TOTP gate — Google Workspace 2FA is the
          // second factor (only staff-domain accounts can get a Google session, and
          // that's gated by the flag). Password sessions keep the portal authenticator.
          if (
            isMfaGatedRole(u.role) &&
            !(ssoOn && provider === "google") &&
            (await needsMfaEnrollment(client))
          ) {
            setUser(u);
            setMfaRequired(true);
          } else {
            setUser(u);
            // No `page` to set on restore: <Routes> renders whatever the address bar
            // holds. A top-level deep link (e.g. /admin) renders that screen (guarded
            // by RequireAccess); a fresh "/" load hits the <Routes> catch-all and
            // redirects to defaultScreenFor; a /requests/... deep link is resolved by
            // the URL→state sync below.
            if (u.sessionReady) {
              loadRequests(u);
              // Load the task-list badge counts by the SAME job rule the nav uses and
              // the login path (onPostLoginComplete) uses — so a superuser / combined /
              // picker gets their Responder + Picker badges on RESTORE (page refresh or
              // reopening an existing session), not only after clicking the tab. The old
              // literal `role === STAFF|ADMIN` check missed superuser/combined/picker, so
              // their header badges stayed at 0 on refresh until the list page was opened.
              if (isResponderJob(u)) loadUnactionedTasks();
              if (isPickerJob(u)) loadPickerTasks();
              if (canAccess(u, "top_up_tasks")) loadTopUpTasks();
            }
          }
        }
      }
      setLoading(false);
    });
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setUser(null);
        setRequests([]);
      }
      // Detect password-reset link click -- Supabase fires PASSWORD_RECOVERY when a
      // recovery-token URL is loaded. We surface a prompt so the user can actually
      // set a new password instead of silently landing on the dashboard.
      if (_event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Password recovery flow -- set when user clicks reset link in email.
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [recoveryNeedsMfa, setRecoveryNeedsMfa] = useState(false);
  const [pendingNewPassword, setPendingNewPassword] = useState("");
  const { submitNewPassword, onMfaChallengeSuccessForRecovery } = usePasswordRecovery({
    pendingNewPassword,
    setPasswordRecovery,
    setRecoveryNeedsMfa,
    setPendingNewPassword,
  });

  // ── Realtime: keep the shared requests cache live for the LIST views ──────
  // The detail page already auto-updates via its own subscription. This app-level
  // channel covers the LIST views (Responders Task List, All Requests, My Requests) so
  // they stay current even when the user is on a list page rather than a detail page.
  // Three triggers: an OSD code landing on children_uniforms; any change to the
  // requests table itself (C3, pragmatic — All Requests + every requests-context
  // consumer goes live from this one subscription instead of a bespoke per-screen hook);
  // and any change to request_tasks → refresh the nav BADGE counts app-wide, so a badge
  // for a list you're NOT viewing goes live too (not just on nav / the in-page 30s poll).
  // The list triggers refetch the shared list; loadRequests + the count loaders are
  // coalesced in usePortalData and the sameRows compare (A1) bails an unchanged result, so
  // there is no churn. Requires requests + request_tasks in the supabase_realtime
  // publication (db-192); fails open to the in-page poll otherwise.
  useEffect(
    function () {
      if (!user?.id) return;
      var channel = getSupabaseClient()
        .channel("list-realtime")
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "children_uniforms",
          },
          function (payload) {
            var newRow = payload.new || {};
            var oldRow = payload.old || {};
            // Only react when osd_code transitioned from empty to a value
            if (!newRow.osd_code) return;
            if (oldRow.osd_code === newRow.osd_code) return;
            // Refetch the whole list so any open list view picks up the change
            loadRequests(user);
          }
        )
        .on("postgres_changes", { event: "*", schema: "public", table: "requests" }, function () {
          // Any request insert/update/delete → refresh the shared cache (All Requests).
          loadRequests(user);
        })
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "request_tasks" },
          function () {
            // Keep the nav badge counts LIVE app-wide (not just on nav / while a list is
            // open). Same access gates as onHeaderNavigate; the count loaders are coalesced
            // so a burst of task changes collapses to one refresh each.
            if (isResponderJob(user)) loadUnactionedTasks();
            if (isPickerJob(user)) loadPickerTasks();
            if (canAccess(user, "top_up_tasks")) loadTopUpTasks();
          }
        )
        .subscribe();
      return function () {
        getSupabaseClient().removeChannel(channel);
      };
    },
    [user?.id]
  );

  // Stage 1 helper: persist a request's children array via the

  // dbToReq (DB row → app object, status re-derived on load) and reqToDb
  // (app object → DB row) now live in src/utils/helpers.ts so the load/save
  // mapping can be unit-tested. Imported at the top of this file.

  const { login, logout } = useAuthSession({
    setUser,
    setSel,
    setNewReq,
    setEditingDraft,
    setMfaRequired,
    setMfaChallenge,
    // `page` is gone; the hook's setPage(default) now navigates to /default —
    // except when a signed-out deep link is still pending, in which case we leave
    // the address bar on /requests/:id so the URL→state sync resolves it after login.
    setPage: function (screen) {
      if (pendingDeepLink.current) return;
      navTo(screen);
    },
    setRequests,
    setTodoReturnPage,
    loadRequests,
    loadUnactionedTasks,
    loadPickerTasks,
    loadTopUpTasks,
  });

  // Load the configurable idle-logout periods once. Absent/failed → hook defaults.
  useEffect(function () {
    getConfigValue(SESSION_TIMEOUT_KEY).then(function (val) {
      setSessionTimeoutCfg(val);
    });
  }, []);

  // The "+ New Request" switch (System Setup → Config). Loaded once here so the
  // button and the /requests/new route agree. FAIL-OPEN: a failed read leaves
  // intake enabled — see services/newRequestSwitch.ts.
  useEffect(function () {
    getConfigValue(NEW_REQUESTS_KEY).then(function (val) {
      setNewRequestsEnabled(normalizeNewRequestsEnabled(val));
    });
  }, []);

  // Idle-session handling (draft auto-save + warn, then logout). The periods are
  // admin-configurable per audience (DFV Service vs Zephyr staff); resolved to
  // ms for the current user here and passed to the tested hook
  // (src/hooks/useInactivityLogout.ts). Null config → the hook's 25/30 defaults.
  const sessionTimers = timersForUser(user, sessionTimeoutCfg);
  useInactivityLogout({
    user,
    saveDraftRef,
    onLogout: logout,
    setSessionWarning,
    warnMs: sessionTimers.warnMs,
    logoutMs: sessionTimers.logoutMs,
  });

  // Open a request from a task list: navigation is the URL now. The detail view
  // resolves /requests/:id (fetching it below if it's a deep link not yet loaded).
  function openRequest(reqId, refNo, soNumber) {
    var found = findRequestInList(requests, reqId, refNo);
    var id = reqId || found?.id;
    // A picker opens a request SCOPED to one Sales Order — carry the SO number in the
    // URL (?picker_so=) so the picker view shows/releases/completes just that SO (db-118).
    // ReqDetail reads it off window.location.search (same pattern as ?comments=1).
    if (id)
      navigate(
        soNumber ? `/requests/${id}?picker_so=${encodeURIComponent(soNumber)}` : `/requests/${id}`
      );
  }

  // ── Deep-link fetch ─────────────────────────────────────────────────────────
  // <Routes>/early-returns render sub-views straight from the URL; the only async
  // piece left is fetching a /requests/:id (or .../edit) not in the list yet (a
  // deep link, or another shelter's request opened from a task). Waits until signed
  // in + ready so the lookup is authorised. Replaces the old viewRequest fetch.
  var fetchingRef = React.useRef(null); // id of an in-flight deep-link fetch (no double-fetch)
  useEffect(
    function () {
      if (!user || user.sessionReady === false) return;
      var m = location.pathname.match(/^\/requests\/([^/]+)(?:\/edit)?$/);
      if (!m || m[1] === "new") return;
      var id = m[1];
      if (resolveReq(id)) return; // already in the list (string-tolerant match)
      if (fetchingRef.current === id) return; // fetch already in flight for this id
      fetchingRef.current = id;
      getRequestViewRowById(id).then(function (row) {
        fetchingRef.current = null;
        if (row) {
          var fetched = dbToReq(row);
          // Add to the list so it's the SAME object the persistence handlers
          // update in place (matches the old viewRequest behaviour).
          setRequests(function (prev) {
            return [fetched].concat(
              prev.filter(function (r) {
                return r.id !== fetched.id;
              })
            );
          });
        } else {
          navTo("admin"); // not found / no access
        }
      });
    },
    [location.pathname, requests, user]
  );
  // Once signed in + ready, stop holding a signed-out deep link (it now renders
  // straight from the URL); after this, navigation behaves normally.
  useEffect(
    function () {
      if (user && user.sessionReady !== false) pendingDeepLink.current = null;
    },
    [user]
  );

  // ── Gate-screen handlers (the screens render via <SessionGate> at the end) ────
  // The pre-app screens (loading / Login / password recovery / MFA / post-login)
  // now live in src/app/SessionGate.jsx; App keeps the state + these handlers and
  // hands them in. Behaviour is unchanged from the old early-return stack.
  function onMfaSetupComplete() {
    setMfaRequired(false);
    setSel(null);
    setNewReq(false);
    setEditingDraft(null);
    // Remember MFA for 30 days on this device
    try {
      localStorage.setItem(`zephyr_mfa_${user.email}`, String(Date.now()));
    } catch (_e) {}
    // Don't set page here - let PostLoginSelection handle it
    user.sessionReady = false; // Force PostLoginSelection to show
    setUser(Object.assign({}, user));
  }
  function onMfaChallengeSuccess(shouldRemember) {
    setMfaChallenge(false);
    setSel(null);
    setNewReq(false);
    setEditingDraft(null);
    // Remember MFA for 30 days on this device (only if checkbox was ticked)
    if (shouldRemember !== false) {
      try {
        localStorage.setItem(`zephyr_mfa_${user.email}`, String(Date.now()));
      } catch (_e) {}
    }
    // Don't set page here - let PostLoginSelection handle it
    user.sessionReady = false; // Force PostLoginSelection to show
    setUser(Object.assign({}, user));
  }
  function onMfaChallengeCancel() {
    setMfaChallenge(false);
    setUser(null);
    getSupabaseClient().auth.signOut();
  }
  function onRecoveryMfaCancel() {
    setRecoveryNeedsMfa(false);
    setPendingNewPassword("");
  }
  function onPostLoginComplete(updatedUser) {
    updatedUser.sessionReady = true;
    setUser(updatedUser);
    // Land on the appropriate default screen based on role/mode (unless a
    // signed-out deep link is pending: then the URL->state sync resolves it).
    if (!pendingDeepLink.current) navTo(defaultPageForUser(updatedUser));
    // Load the task-list badge counts by the SAME rule the nav uses for visibility,
    // so a superuser / combined user gets their Responder + Picker badges on landing
    // (not only after clicking the tab). isResponderJob/isPickerJob cover superuser +
    // combined, which the old literal role checks missed.
    loadRequests(updatedUser);
    if (isResponderJob(updatedUser)) loadUnactionedTasks();
    if (isPickerJob(updatedUser)) loadPickerTasks();
    if (canAccess(updatedUser, "top_up_tasks")) loadTopUpTasks();
  }

  // (recovery / MFA / post-login screens render via <SessionGate> at the end)

  // ── App-shell handlers (the shell itself is src/app/AppShell.jsx) ────────────
  // The providers + header + security panel + session-warning banner moved into
  // <AppShell> (Phase 5). App keeps the state and passes the provider values +
  // these handlers; the shell renders the active screen/sub-view as its children.
  function onHeaderNavigate(p) {
    setSel(null);
    setNewReq(false);
    navTo(p);
    if (p === "todo") loadUnactionedTasks();
    if (p === "pickers") loadPickerTasks();
    if (p === "top_up_tasks") loadTopUpTasks();
  }

  // Memoized context values (A2, live-lists overhaul): built once per change of their
  // members rather than fresh every render, so an unrelated re-render (e.g. a poll that
  // DID change one list) no longer re-renders every Session/Requests consumer — the
  // Header, the nav badges, and the other list screens. Every member is in the dep array,
  // so a memoized value can never be stale. Relies on the stable loader identities above.
  const sessionValue = React.useMemo(
    function () {
      return { user, login, logout };
    },
    [user, login, logout]
  );
  const requestsValue = React.useMemo(
    function () {
      return {
        requests,
        loadRequests,
        unactionedTaskCount,
        pickerTaskCount,
        topUpTaskCount,
        loadUnactionedTasks,
        loadPickerTasks,
        loadTopUpTasks,
      };
    },
    [
      requests,
      loadRequests,
      unactionedTaskCount,
      pickerTaskCount,
      topUpTaskCount,
      loadUnactionedTasks,
      loadPickerTasks,
      loadTopUpTasks,
    ]
  );

  // Request sub-views render from the URL (Phase 4 2b). Each keys on the URL match
  // (not the resolved object) so a deep-linked /requests/:id still being fetched
  // shows a loading state instead of falling through to the catch-all. The chosen
  // body is built here and handed to <SessionGate>, which renders it (wrapped in the
  // app shell) only once the user is signed in + ready — otherwise it shows the
  // matching gate screen.
  var appBody;
  // Stock Top-ups Phase 5: /requests/new?prefill=1 is the deep-link / dashboard-banner
  // target — load THIS service's own pending staff-prepared prefill and open the wizard
  // pre-filled + read-only. It bypasses the intake-closed gate below because a staffer
  // preparing it IS the authorization (like the type-chooser availability switch).
  const topUpPrefill = newReq && new URLSearchParams(location.search).get("prefill") === "1";
  // Intake closed (System Setup → Config): the button is hidden, but a bookmark or
  // an already-open tab could still land here — send them back to their list
  // rather than showing the wizard. Drafts + a staff-prepared prefill are unaffected.
  if (newReq && !newRequestsEnabled && !topUpPrefill)
    appBody = <Navigate to={returnTarget()} replace />;
  else if (newReq) {
    const onWizardBack = function () {
      pendingDraftIdRef.current = null;
      pendingDraftRefNoRef.current = null;
      navigate(returnTarget());
    };
    const getPendingDraftId = function () {
      return pendingDraftIdRef.current;
    };
    appBody = topUpPrefill ? (
      <TopUpPrefillWizard
        user={user}
        onBack={onWizardBack}
        onSaveDraft={saveDraft}
        saveDraftRef={saveDraftRef}
        getPendingDraftId={getPendingDraftId}
      />
    ) : (
      <RequestWizard
        onBack={onWizardBack}
        onSaveDraft={saveDraft}
        saveDraftRef={saveDraftRef}
        getPendingDraftId={getPendingDraftId}
      />
    );
  } else if (editMatch)
    appBody = editingDraft ? (
      <RequestWizard
        onBack={() => navigate(`/requests/${editingDraft.id}`)}
        onSaveDraft={updateDraft}
        initialData={editingDraft}
        draftId={editingDraft.id}
        draftRefNo={editingDraft.refNo}
        saveDraftRef={saveDraftRef}
      />
    ) : (
      <RequestLoading />
    );
  else if (detailMatch)
    appBody = selected ? (
      <ReqDetail
        req={selected}
        onBack={function () {
          navigate(returnTarget());
          // Refresh request list so shelter sees any new docs/comments
          if (user && user.role === ROLES.SHELTER) loadRequests(user);
        }}
        onSave={updateRequest}
        onSaveNoDb={function (r) {
          updateRequest(r, true);
        }}
        onContinueEdit={(r) => navigate(`/requests/${r.id}/edit`)}
        fromTodo={(lastTopLevelPathRef.current || "").replace(/^\//, "")}
        onDeleteDraft={function (id) {
          setRequests(function (prev) {
            return prev.filter(function (r) {
              return r.id !== id;
            });
          });
          // db-209: the deleted draft's id must not survive as the wizard's pending
          // draft, or the next submit would UPDATE the soft-deleted row (invisible
          // to everyone) instead of inserting a fresh request.
          if (pendingDraftIdRef.current === id) {
            pendingDraftIdRef.current = null;
            pendingDraftRefNoRef.current = null;
          }
          navigate(returnTarget());
        }}
      />
    ) : (
      <RequestLoading />
    );
  // Top-level screens are now a declarative route table (Phase 4 slice 2a-i).
  // Each element is wrapped in RequireAccess so a disallowed screen redirects to
  // the user's default (the same access.ts policy as the nav), replacing the old
  // page-ternary's unguarded ShelterDash fall-through. The `page` state + URL sync
  // still drive navigation for now (slice 2a-ii retires `page`); the routes simply
  // render from the URL that sync keeps in step. The request sub-views
  // (newReq/editingDraft/selected) are handled by the branches above.
  else
    appBody = (
      <Routes>
        <Route
          path="/dashboard"
          element={
            <RequireAccess screen="dashboard">
              <ShelterDash
                onNew={() => setNewReq(true)}
                onSelect={setSel}
                canCreate={newRequestsEnabled}
                onReviewPrefill={() => navigate("/requests/new?prefill=1")}
              />
            </RequireAccess>
          }
        />
        <Route
          path="/todo"
          element={
            <RequireAccess screen="todo">
              <ToDoPage onViewRequest={(reqId, refNo) => openRequest(reqId, refNo)} />
            </RequireAccess>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAccess screen="admin">
              <AdminDash
                onSelect={setSel}
                onNew={() => setNewReq(true)}
                defaultShelterFilter={location.state?.shelterFilter}
                onFilterApplied={function () {
                  // Clear the router state so the filter doesn't re-apply on refresh/back.
                  navigate(location.pathname, { replace: true, state: null });
                }}
              />
            </RequireAccess>
          }
        />
        <Route
          path="/pickers"
          element={
            <RequireAccess screen="pickers">
              {/* Pass ALL three args through (reqId, refNo, soNumber) so a picker opens
                  scoped to the task's SO (?picker_so=) — the 2-arg wrapper dropped the
                  SO number, so the picker view showed every SO's items (26 Jul fix). */}
              <PickersPage onViewRequest={openRequest} />
            </RequireAccess>
          }
        />
        <Route
          path="/top_up_tasks"
          element={
            <RequireAccess screen="top_up_tasks">
              <TopUpTaskListPage />
            </RequireAccess>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAccess screen="reports">
              <ReportsPage requests={requests} />
            </RequireAccess>
          }
        />
        <Route
          path="/statistics"
          element={
            <RequireAccess screen="statistics">
              <StatisticsPage requests={requests} />
            </RequireAccess>
          }
        />
        <Route
          path="/templates"
          element={
            <RequireAccess screen="templates">
              <TemplatesAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/users"
          element={
            <RequireAccess screen="users">
              <UserAdmin />
            </RequireAccess>
          }
        />
        {/* Full-screen shelter detail (shelter-admin redesign Phase 1). Same
            component: UserAdmin reads :shelterCode via useParams and swaps the
            list for ShelterDetail, so list state survives the round trip. */}
        <Route
          path="/users/:shelterCode"
          element={
            <RequireAccess screen="users">
              <UserAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/staff"
          element={
            <RequireAccess screen="staff">
              <ZephyrStaffAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/import"
          element={
            <RequireAccess screen="import">
              <ImportPage />
            </RequireAccess>
          }
        />
        <Route
          path="/invites"
          element={
            <RequireAccess screen="invites">
              <InvitesAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/push_queue"
          element={
            <RequireAccess screen="push_queue">
              <UnleashedPushListAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/audit"
          element={
            <RequireAccess screen="audit">
              <AuditLogPage />
            </RequireAccess>
          }
        />
        <Route
          path="/suppliers"
          element={
            <RequireAccess screen="suppliers">
              <SupplierAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/schools"
          element={
            <RequireAccess screen="schools">
              <SchoolsAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/lookups"
          element={
            <RequireAccess screen="lookups">
              <LookupListsAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/setup"
          element={
            <RequireAccess screen="setup">
              <SystemSetupAdmin user={user} />
            </RequireAccess>
          }
        />
        <Route
          path="/products"
          element={
            <RequireAccess screen="products">
              <UnleashedProductsAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/product_map"
          element={
            <RequireAccess screen="product_map">
              <ProductMappingAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/so_rules"
          element={
            <RequireAccess screen="so_rules">
              <SORulesAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/sales_orders"
          element={
            <RequireAccess screen="sales_orders">
              <SalesOrdersAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/top_up_product_map"
          element={
            <RequireAccess screen="top_up_product_map">
              <TopUpProductMappingAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/top_up_so_rules"
          element={
            <RequireAccess screen="top_up_so_rules">
              <TopUpSORulesAdmin />
            </RequireAccess>
          }
        />
        <Route
          path="/permissions"
          element={
            <RequireAccess screen="permissions">
              <AdminPermissionsMatrix />
            </RequireAccess>
          }
        />
        <Route
          path="/staff_list"
          element={
            <RequireAccess screen="staff_list">
              <StaffDirectory />
            </RequireAccess>
          }
        />
        <Route
          path="/calendar"
          element={
            <RequireAccess screen="calendar">
              <CalendarPage user={user} />
            </RequireAccess>
          }
        />
        <Route
          path="/resource_content"
          element={
            <RequireAccess screen="resource_content">
              <ResourcePagesAdmin user={user} />
            </RequireAccess>
          }
        />
        <Route
          path="/general"
          element={
            <RequireAccess screen="general">
              <GeneralPage />
            </RequireAccess>
          }
        />
        <Route
          path="/invoicing"
          element={
            <RequireAccess screen="invoicing">
              <InvoicingPage />
            </RequireAccess>
          }
        />
        <Route
          path="/responders"
          element={
            <RequireAccess screen="responders">
              <RespondersPage />
            </RequireAccess>
          }
        />
        {/* Resources document links (db-138). NOT a nav destination, so it has no
            ScreenId and no RequireAccess — the component guards on isInternalUser,
            and RLS + the storage policy are the real gate. */}
        <Route path="/resource-doc/:id" element={<ResourceDocRedirect user={user} />} />
        <Route path="*" element={<Navigate to={`/${defaultScreenFor(user)}`} replace />} />
      </Routes>
    );

  // The gate screens (loading / Login / recovery / MFA / post-login) render here;
  // once the user is signed in + ready, SessionGate renders the app shell instead.
  return (
    <SessionGate
      loading={loading}
      user={user}
      mfaRequired={mfaRequired}
      mfaChallenge={mfaChallenge}
      passwordRecovery={passwordRecovery}
      recoveryNeedsMfa={recoveryNeedsMfa}
      onLogin={login}
      onRecoveryMfaSuccess={onMfaChallengeSuccessForRecovery}
      onRecoveryMfaCancel={onRecoveryMfaCancel}
      onSubmitNewPassword={submitNewPassword}
      onMfaSetupComplete={onMfaSetupComplete}
      onMfaChallengeSuccess={onMfaChallengeSuccess}
      onMfaChallengeCancel={onMfaChallengeCancel}
      onPostLoginComplete={onPostLoginComplete}
    >
      <AppShell
        sessionValue={sessionValue}
        requestsValue={requestsValue}
        onNavigate={onHeaderNavigate}
        onShowSecurity={function () {
          setShowSecurity(true);
        }}
        showSecurity={showSecurity}
        onCloseSecurity={function () {
          setShowSecurity(false);
        }}
        sessionWarning={sessionWarning}
        sessionWarnMinutes={warnLeadMinutesForUser(user, sessionTimeoutCfg)}
        onDismissWarning={function () {
          setSessionWarning(false);
        }}
      >
        {appBody}
      </AppShell>
    </SessionGate>
  );
}
